import {
  DEFAULT_CLIENT_SETTINGS,
  type ConfirmDialogOptions,
  type ContextMenuItem,
  type LocalApi,
} from "@cleo/contracts";

import { requestConfirmDialog } from "./confirmDialog";
import { dismissContextMenu, showContextMenuFallback } from "./contextMenuFallback";
import { readBrowserClientSettings, writeBrowserClientSettings } from "./clientPersistenceStorage";
import { resetRequestLatencyStateForTests } from "./rpc/requestLatencyState";

let cachedApi: LocalApi | undefined;

function createBrowserLocalApi(): LocalApi {
  const bridgeRequestAccessibility = window.desktopBridge?.requestAccessibility;
  const bridgeRequestScreenRecording = window.desktopBridge?.requestScreenRecording;
  return {
    dialogs: {
      pickFolder: async (options) => {
        if (!window.desktopBridge) return null;
        return window.desktopBridge.pickFolder(options);
      },
      confirm: async (message, options?: ConfirmDialogOptions) => {
        return requestConfirmDialog(message, options) ?? false;
      },
    },
    permissions: {
      // Presence-gated: the bridge method exists only in desktop builds that
      // ship it, and the API is created after preload has run, so checking
      // once here is stable for the app's lifetime.
      requestAccessibility: bridgeRequestAccessibility ? () => bridgeRequestAccessibility() : null,
      requestScreenRecording: bridgeRequestScreenRecording
        ? () => bridgeRequestScreenRecording()
        : null,
    },
    shell: {
      openExternal: async (url) => {
        if (window.desktopBridge) {
          const opened = await window.desktopBridge.openExternal(url);
          if (!opened) {
            throw new Error("Unable to open link.");
          }
          return;
        }

        window.open(url, "_blank", "noopener,noreferrer");
      },
      focusWindow: async () => {
        if (window.desktopBridge?.focusWindow) {
          await window.desktopBridge.focusWindow();
          return;
        }
        window.focus();
      },
    },
    contextMenu: {
      show: async <T extends string>(
        items: readonly ContextMenuItem<T>[],
        position?: { x: number; y: number },
      ): Promise<T | null> => {
        if (window.desktopBridge) {
          return window.desktopBridge.showContextMenu(items, position) as Promise<T | null>;
        }
        return showContextMenuFallback(items, position);
      },
      // A native desktop menu blocks keyboard input and closes on outside
      // interaction, so nothing to do there; the DOM fallback needs an explicit
      // dismiss when the state behind it goes away.
      close: async () => {
        if (!window.desktopBridge) {
          dismissContextMenu();
        }
      },
    },
    persistence: {
      getClientSettings: async () => {
        if (window.desktopBridge) {
          return window.desktopBridge.getClientSettings();
        }
        return readBrowserClientSettings();
      },
      setClientSettings: async (settings) => {
        if (window.desktopBridge) {
          return window.desktopBridge.setClientSettings(settings);
        }
        writeBrowserClientSettings(settings);
      },
      patchClientSettings: async (patch) => {
        const bridge = window.desktopBridge;
        if (bridge) {
          if (typeof bridge.patchClientSettings === "function") {
            return bridge.patchClientSettings(patch);
          }
          // An older preload: merge over what the Desk holds now, not over
          // this window's snapshot.
          const current = await bridge.getClientSettings();
          return bridge.setClientSettings({ ...DEFAULT_CLIENT_SETTINGS, ...current, ...patch });
        }
        writeBrowserClientSettings({
          ...DEFAULT_CLIENT_SETTINGS,
          ...readBrowserClientSettings(),
          ...patch,
        });
      },
      onClientSettingsChange: (listener) => {
        const subscribe = window.desktopBridge?.onClientSettingsChange;
        if (typeof subscribe !== "function") return () => {};
        return subscribe(listener);
      },
    },
  };
}

export function createLocalApi(): LocalApi {
  return createBrowserLocalApi();
}

export function readLocalApi(): LocalApi | undefined {
  if (typeof window === "undefined") return undefined;
  if (cachedApi) return cachedApi;

  cachedApi = createLocalApi();
  return cachedApi;
}

export function ensureLocalApi(): LocalApi {
  const api = readLocalApi();
  if (!api) {
    throw new Error("Local API not found");
  }
  return api;
}

export async function __resetLocalApiForTests() {
  cachedApi = undefined;
  const { __resetClientSettingsPersistenceForTests } = await import("./hooks/useSettings");
  __resetClientSettingsPersistenceForTests();
  resetRequestLatencyStateForTests();
}
